import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Progressive enhancement for plain `/cart/add` forms — the GemPages-copied
 * buy boxes, the sticky ATC bar, and the buy-box upsell ADD button. Horizon
 * forms live inside <product-form-component> and are skipped.
 *
 * Instead of the native POST (which navigates to /cart), the item is added
 * over AJAX and a standard CartLinesUpdateEvent is dispatched so the cart
 * drawer contents and header bubble update themselves; the drawer is then
 * opened. Mirrors the flow in product-form.js.
 */

/** @returns {Promise<any>} */
function refreshCart() {
  return fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((response) => {
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  });
}

async function openCartDrawer() {
  await customElements.whenDefined('theme-drawer');
  const drawer = /** @type {HTMLElement & {open?: () => void} | null} */ (
    document.querySelector('theme-drawer#cart-drawer')
  );
  drawer?.open?.();
}

/**
 * @param {HTMLFormElement} form
 * @param {HTMLElement | null} submitter
 */
async function addToCart(form, submitter) {
  const formData = new FormData(form);
  if (!formData.get('quantity')) formData.set('quantity', '1');

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : null))
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const itemCount = Number(formData.get('quantity')) || 1;
  const deferredEventPromise = CartLinesUpdateEvent.createPromise();

  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [
        {
          merchandiseId: /** @type {string} */ (formData.get('id')),
          quantity: itemCount,
        },
      ],
      promise: deferredEventPromise.promise,
    })
  );

  submitter?.setAttribute('data-state', 'loading');
  if (submitter instanceof HTMLButtonElement) submitter.disabled = true;

  const fetchCfg = fetchConfig('javascript', { body: formData });

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: { ...fetchCfg.headers, Accept: 'text/html' },
    }).then((res) => res.json());

    if (response.status) {
      form.dispatchEvent(
        new CartErrorEvent({
          error: response.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: response.description, errors: response.errors },
        })
      );

      const ajaxCart = await refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: true,
          items: ajaxCart.items,
          source: 'cart-add-form',
          sourceId: form.id,
          itemCount,
        },
      });
      console.warn('[cart-add-forms]', response.message || 'Add to cart failed');
      return;
    }

    // Open right away for instant feedback unless the drawer still shows the
    // empty state — then wait for the content morph to avoid a brief
    // "your cart is empty" flash (mirrors cart-drawer's auto-open).
    const drawerIsEmpty = Boolean(document.querySelector('theme-drawer#cart-drawer .cart-drawer--empty'));
    if (!drawerIsEmpty) openCartDrawer();

    const ajaxCart = await refreshCart();
    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        didError: false,
        items: ajaxCart.items,
        source: 'cart-add-form',
        sourceId: form.id,
        itemCount,
        sections: response.sections,
      },
    });

    if (drawerIsEmpty) openCartDrawer();
  } catch (error) {
    deferredEventPromise.reject(error);
    form.dispatchEvent(
      new CartErrorEvent({
        error: error?.message || 'Network error during add to cart',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
    console.error(error);
  } finally {
    submitter?.setAttribute('data-state', 'idle');
    if (submitter instanceof HTMLButtonElement) submitter.disabled = false;
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (event.defaultPrevented) return;
  if (!(form.getAttribute('action') || '').includes('/cart/add')) return;
  // Horizon's product-form-component already handles its own forms over AJAX.
  if (form.closest('product-form-component')) return;
  // No drawer on the page (e.g. cart type "page") — keep the native submit.
  if (!document.querySelector('theme-drawer#cart-drawer')) return;

  event.preventDefault();
  addToCart(form, event.submitter instanceof HTMLElement ? event.submitter : null);
});
